import { FORM_ITEMS } from '../constants/form_items';
import { Dispatch, SetStateAction } from 'react';
import { Company } from '../../models/company.model';

interface Obj {
  [key: string]: string | object;
}

interface ResponseDataObject extends Obj {
  message: string;
}

interface ResponseObject extends Obj {
  data: ResponseDataObject;
}

export interface ErrorObject extends Obj {
  response: ResponseObject;
  request: Obj;
}

export const Helpers = {
  handleRequestError: (
    errorData: ResponseObject,
    setErrorTextArray: (
      errorTextArray: { [key: string]: string | object }[]
    ) => void | Dispatch<SetStateAction<{ [key: string]: string | object }[]>>,
    setPendingStatus: (flag: boolean) => void
  ) => {
    const errorTextArray = [];
    const obj = errorData.data;
    if (typeof obj === 'object') {
      Object.keys(obj).forEach((el) => {
        const errorItem = {
          [el]: obj[el],
        };
        errorTextArray.push(errorItem);
      });
    } else if (errorData.statusText) {
      errorTextArray.push({ statusText: errorData.statusText });
    } else {
      errorTextArray.push({ errorText: FORM_ITEMS.BASIC_ERROR_TEXT });
    }
    setErrorTextArray(errorTextArray);
    setPendingStatus(false);
  },
  formatDBDateToUI: (date: string) =>
    date ? date.replace(/(\d{4})-(\d{2})-(\d{2}).*/, '$3.$2.$1') : null,
  returnErrorText: (error: ErrorObject) => {
    const errorMessage =
      error.response && error.response.data && error.response.data.message
        ? error.response.data.message
        : error.message
          ? error.message
          : error.request.statusText;
    return errorMessage;
  },
  a11yProps(index: number, prefix: string) {
    return {
      id: `${prefix}-tab-${index}`,
      'aria-controls': `${prefix}-tabpanel-${index}`,
    };
  },
  timeStampToDate(timeStamp: string): string {
    return (
      timeStamp.substring(8, 10) + '.' + timeStamp.substring(5, 7) + '.' + timeStamp.substring(0, 4)
    );
  },
  timeStampToDateV3(timeStamp: string): string {
    return (
      timeStamp.substring(8, 10) + '/' + timeStamp.substring(5, 7) + '/' + timeStamp.substring(0, 4)
    );
  },
  timeStampToDateV4(timeStamp: string): string {
    return timeStamp.substring(0, 10) + 'T' + timeStamp.substring(11, 16);
  },
  capitalizeTitle(title: string) {
    const exceptions = ['and', 'or'];
    return title
      .split(' ')
      .map((word) =>
        exceptions.includes(word.toLowerCase())
          ? word
          : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join(' ');
  },
  timeStampToDateV2(timeStamp: string | undefined | null): string {
    if (!timeStamp) {
      return '-';
    }
    const options = { year: '2-digit', month: 'short', day: 'numeric' };

    return (
      // @ts-expect-error: should be fine with options
      new Date(timeStamp).toLocaleDateString('en-AU', options)
    );
  },
  capitalizeFirstLetter(item: string) {
    return item.charAt(0).toUpperCase() + item.slice(1);
  },
  lowercaseFirstLetter(item: string) {
    return item.charAt(0).toLowerCase() + item.slice(1);
  },
  getPlanSettings(company: Company) {
    if (company) {
      const { stripe_product_id, plan_settings } = company;

      if (!stripe_product_id) {
        return typeof plan_settings.free === 'object' ? plan_settings.free : null;
      }
      if (stripe_product_id && !plan_settings[stripe_product_id]) {
        return typeof plan_settings.legacy === 'object' ? plan_settings.legacy : null;
      }
      if (stripe_product_id && plan_settings[stripe_product_id]) {
        const plan = plan_settings[stripe_product_id];
        return typeof plan === 'object' ? plan : null;
      }
    }
    return null;
  },
  convertHtmlToPdfAndDownload(htmlString: string, filename = 'document.pdf') {
    // Open a new window with the HTML content
    const printWindow = window.open('', '_blank');

    printWindow.document.write(`
    <!DOCTYPE html>
    <html>
      <head>
        <meta charset="utf-8" />
        <title>${filename}</title>
        <style>
          /* Reset & base */
          * { box-sizing: border-box; margin: 0; padding: 0; }
          body { font-family: Arial, sans-serif; font-size: 13px; line-height: initial; color: #000; }

          /* Hide UI chrome when printing */
          @media print {
            body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          }
        </style>
      </head>
      <body>
        ${htmlString}
      </body>
    </html>
  `);

    printWindow.document.close();

    // Wait for resources to load, then trigger the print/save dialog
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print(); // Opens "Save as PDF" in the browser print dialog
      printWindow.close();
    };
  },
};
